import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../lib/auth';
import api, { staffAPI, departmentsAPI, doctorAPI, rolesAPI, wardsAPI, nurseAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/PhoneInput';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { StaffIDCardModal, WelcomeEmailModal, printBulkIDCards } from './StaffPrintTools';
import {
  Users, Plus, Search, Phone, Mail, Stethoscope, UserCog, Briefcase, Database, ShieldAlert,
  IdCard, MapPin, Heart, AlertCircle, Building2, Calendar as CalendarIcon, BadgeCheck,
  Printer, Send, Copy, Receipt, Calculator, Headset, ClipboardCheck, Pencil, Trash2, KeyRound, ShieldCheck,
  Upload, FileDown, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Compass,
} from 'lucide-react';

const FALLBACK_DEPTS = ['Administration', 'Medical', 'Nursing', 'Finance', 'HR', 'IT', 'Front Desk', 'Billing', 'Pharmacy', 'Lab', 'Radiology', 'Housekeeping', 'Security'];

const StaffCard = ({ staff, onView, onEdit, onDelete, canDelete, selected, onToggleSelect, selectMode }) => {
  const u = staff.user || {};
  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`;
  const statusColor = staff.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400';
  return (
    <div className={`flex items-center gap-4 p-4 bg-white rounded-xl border transition-all ${selected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-300 hover:shadow-md'} ${selectMode ? '' : 'cursor-pointer'}`}
      onClick={() => selectMode ? onToggleSelect(staff.id) : onView(staff)} data-testid={`staff-card-${staff.id}`}>
      {selectMode && (
        <input type="checkbox" checked={selected} onChange={() => onToggleSelect(staff.id)} onClick={e => e.stopPropagation()}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0" data-testid={`staff-checkbox-${staff.id}`} />
      )}
      <div className="relative flex-shrink-0">
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-100 to-lime-100 flex items-center justify-center text-emerald-700 font-bold text-lg border-2 border-emerald-200">
          {initials}
        </div>
        <span className={`absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white ${statusColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{u.first_name} {u.last_name}</h3>
        <p className="text-sm text-slate-500 truncate">{staff.designation} · {staff.department}</p>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {u.phone && <span className="text-xs text-slate-400 flex items-center gap-1"><Phone className="h-3 w-3" /> {u.phone}</span>}
          <span className="text-xs text-slate-400 font-mono">{staff.employee_code}</span>
          {u.role_name && <span className="text-xs text-emerald-600 font-medium">{u.role_name}</span>}
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        <Badge className={staff.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
          {staff.status}
        </Badge>
        {!selectMode && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50" onClick={() => onEdit(staff)} title="Edit staff" data-testid={`staff-edit-${staff.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            {canDelete && (
              <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(staff)} title="Delete staff" data-testid={`staff-delete-${staff.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StaffList = ({ staffList, searchQuery, onView, onEdit, onDelete, canDelete, emptyMessage, selectedIds, onToggleSelect, selectMode }) => {
  const filtered = staffList.filter(s => {
    const u = s.user || {};
    const q = searchQuery.toLowerCase();
    return (
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.includes(q) ||
      s.employee_code?.toLowerCase().includes(q) ||
      s.department?.toLowerCase().includes(q) ||
      s.designation?.toLowerCase().includes(q)
    );
  });
  if (!filtered.length) {
    return <div className="bg-white rounded-xl border border-dashed border-slate-200 p-12 text-center text-slate-400">{emptyMessage}</div>;
  }
  return (
    <div className="grid gap-3">
      {filtered.map(s => <StaffCard key={s.id} staff={s} onView={onView} onEdit={onEdit} onDelete={onDelete} canDelete={canDelete}
        selected={selectedIds?.has(s.id)} onToggleSelect={onToggleSelect} selectMode={selectMode} />)}
    </div>
  );
};

const PROOF_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'other', label: 'Other Govt ID' },
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

// v38.12 — Curated designation master. Future HRMS will swap this for the
// hospital-scoped designation table that drives the pay-scale mapping.
const DESIGNATIONS = [
  'HOD', 'Senior Consultant', 'Consultant', 'Junior Consultant', 'Visiting Consultant',
  'Senior Resident', 'Resident Doctor', 'Junior Resident', 'Intern / House Surgeon',
  'Nursing Superintendent', 'Senior Nurse', 'Head Nurse', 'Staff Nurse', 'ICU Nurse',
  'OT Nurse', 'Nurse Trainee',
  'Chief Pharmacist', 'Senior Pharmacist', 'Pharmacist', 'Pharmacy Assistant',
  'Senior Lab Technician', 'Lab Technician', 'Phlebotomist',
  'Radiologist', 'Sonologist', 'Senior Radiographer', 'Radiographer',
  'OT Technician', 'CSSD Technician', 'Biomedical Engineer',
  'Front Desk Manager', 'Front Desk Executive', 'Receptionist',
  'Billing Manager', 'Billing Executive', 'Cashier', 'Insurance Coordinator',
  'Finance Manager', 'Senior Accountant', 'Accountant',
  'HR Manager', 'HR Executive',
  'IT Manager', 'IT Executive', 'System Administrator',
  'Housekeeping Supervisor', 'Housekeeping Staff', 'Ward Boy / Ayah',
  'Security Supervisor', 'Security Guard',
  'Driver', 'Cook', 'Dietician', 'Physiotherapist',
  'General Manager', 'Manager', 'Assistant Manager', 'Executive', 'Officer', 'Trainee',
];

// v38.12 — Canonical staff categories (P / C / T) — must match
// backend/lib/employee_id.py::ALLOWED_CATEGORIES.
const STAFF_CATEGORIES = [
  { value: 'Permanent', label: 'Permanent', letter: 'P' },
  { value: 'Contractual', label: 'Contractual', letter: 'C' },
  { value: 'Trainee', label: 'Trainee', letter: 'T' },
];

// Maps role.code → tab icon. Anything unmapped falls back to the generic Users icon.
const ROLE_TAB_ICON = {
  doctor: Stethoscope,
  nurse: Heart,
  reception: Headset,
  front_desk: Headset,
  billing_staff: Receipt,
  hr: Briefcase,
  accountant: Calculator,
  finance: Calculator,
  hospital_admin: ShieldAlert,
  it_admin: ShieldAlert,
  admin: ShieldAlert,
};

const StaffPage = () => {
  const { user, hospital, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wards, setWards] = useState([]);
  const [doctorConfigs, setDoctorConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [idCardStaff, setIdCardStaff] = useState(null);
  const [welcomeEmailStaff, setWelcomeEmailStaff] = useState(null);
  const [editStaff, setEditStaff] = useState(null);  // Staff being edited
  const [deleteStaff, setDeleteStaff] = useState(null); // Staff pending delete confirmation
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);  // 1=Personal, 2=Employment, 3=KYC, 4=Doctor (conditional)
  const initialForm = {
    // Personal
    first_name: '', last_name: '', email: '', phone: '', role_id: '',
    // Employment — v38.12: Department + Category + Designation now on Step 1
    employee_code: '', department: '', department_code: '',
    designation: '', staff_category: 'Permanent',
    joining_date: '',
    // KYC
    address: '', pan: '', aadhaar: '', user_id_proof: '', user_id_proof_type: '',
    blood_group: '', emergency_contact: '', emergency_phone: '',
    // Doctor-specific
    consultation_fee: '', follow_up_fee: '', specialization: '', qualification: '',
    experience_years: '', registration_number: '',
  };
  const [formData, setFormData] = useState(initialForm);
  // v38.12 — Live Employee-ID preview shown on Step 2. Bumps whenever
  // (department, staff_category) change and an explicit override hasn't been typed.
  const [peekId, setPeekId] = useState({ display_id: '', login_id: '' });

  useEffect(() => { fetchData(); }, []);

  // v38.12 — peek the next Employee ID as soon as department + category are picked.
  useEffect(() => {
    const fetchPeek = async () => {
      if (!formData.department_code || !formData.staff_category) {
        setPeekId({ display_id: '', login_id: '' });
        return;
      }
      try {
        // v38.12 — When super-admin is impersonating a tenant via the
        // devTenantSlug override, the JWT has no hospital_id. Fall back to the
        // hospital ID that App.js cached during the tenant bootstrap so the
        // peek endpoint can resolve a counter. Hospital-Admin / Doctor / Nurse
        // sessions ignore this — their JWT already carries the hospital_id.
        const cachedHospitalId = (typeof window !== 'undefined' && localStorage.getItem('selectedHospitalId')) || '';
        const res = await api.get('/staff/peek-employee-id', {
          params: {
            department_code: formData.department_code,
            staff_category: formData.staff_category,
            ...(cachedHospitalId ? { hospital_id: cachedHospitalId } : {}),
          },
        });
        setPeekId(res.data || { display_id: '', login_id: '' });
      } catch (_) {
        setPeekId({ display_id: '', login_id: '' });
      }
    };
    fetchPeek();
  }, [formData.department_code, formData.staff_category]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, rolesRes, deptsRes, docsRes, wardsRes] = await Promise.all([
        staffAPI.list(),
        rolesAPI.list().catch(() => ({ data: [] })),
        departmentsAPI.list().catch(() => ({ data: [] })),
        doctorAPI.list().catch(() => ({ data: [] })),
        wardsAPI.list().catch(() => ({ data: [] })),
      ]);
      setStaff(staffRes.data || []);
      // Filter out super_admin role from the dropdown
      setRoles((rolesRes.data || []).filter(r => r.code !== 'super_admin'));
      setDepartments(deptsRes.data || []);
      setDoctorConfigs(docsRes.data || []);
      setWards(wardsRes.data || []);
    } catch {
      toast.error('Failed to load staff data');
    } finally { setLoading(false); }
  };

  const selectedRole = roles.find(r => r.id === formData.role_id);
  const isDoctorRole = selectedRole?.code === 'doctor' || selectedRole?.name?.toLowerCase() === 'doctor';
  const totalSteps = isDoctorRole ? 4 : 3;

  const set = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));

  const resetForm = () => { setFormData(initialForm); setStep(1); };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.first_name || !formData.last_name) return 'Name is required';
      if (!formData.email || !/.+@.+\..+/.test(formData.email)) return 'Valid email required';
      if (formData.phone && !/^\d{10}$/.test(formData.phone)) return 'Phone must be exactly 10 digits';
      // v38.12 — Department + Employment Type drive Employee ID generation.
      if (!formData.department) return 'Department required';
      if (!formData.staff_category) return 'Employment Type required';
      if (!formData.designation) return 'Designation required';
    }
    if (step === 2) {
      // v38.12 — Employee ID is auto-generated and read-only; only Joining Date is mandatory here.
      if (!formData.joining_date) return 'Joining date required';
    }
    if (step === 3) {
      if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan.toUpperCase())) return 'PAN format: ABCDE1234F';
      if (formData.aadhaar && !/^\d{12}$/.test(formData.aadhaar.replace(/\s/g, ''))) return 'Aadhaar must be 12 digits';
      if (formData.emergency_phone && !/^\d{10}$/.test(formData.emergency_phone)) return 'Emergency phone must be exactly 10 digits';
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep(s => Math.min(s + 1, totalSteps));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        first_name: formData.first_name, last_name: formData.last_name,
        email: formData.email,
        // Backend auto-generates a strong temp password — this placeholder is ignored.
        password: 'AutoGen-' + Math.random().toString(36).slice(2, 10).toUpperCase(),
        phone: formData.phone || null,
        role_id: formData.role_id || null, // v38.12 — Role is optional now.
        employee_code: formData.employee_code ? formData.employee_code.toUpperCase() : null,
        department: formData.department,
        department_code: formData.department_code || null,
        designation: formData.designation,
        joining_date: formData.joining_date,
        // v38.12 — Canonical employment type drives the Employee ID generator.
        staff_category: formData.staff_category,
        // Back-compat: keep `employment_type` mirrored for legacy reads on the staff record.
        employment_type: (formData.staff_category || '').toLowerCase(),
        address: formData.address || null,
        pan: formData.pan ? formData.pan.toUpperCase() : null,
        aadhaar: formData.aadhaar ? formData.aadhaar.replace(/\s/g, '') : null,
        user_id_proof: formData.user_id_proof || null,
        user_id_proof_type: formData.user_id_proof_type || null,
        blood_group: formData.blood_group || null,
        emergency_contact: formData.emergency_contact || null,
        emergency_phone: formData.emergency_phone || null,
      };
      if (isDoctorRole) {
        payload.consultation_fee = formData.consultation_fee ? parseFloat(formData.consultation_fee) : 0;
        payload.follow_up_fee = formData.follow_up_fee ? parseFloat(formData.follow_up_fee) : 0;
        payload.specialization = formData.specialization || null;
        payload.qualification = formData.qualification || null;
        payload.experience_years = formData.experience_years ? parseInt(formData.experience_years) : null;
        payload.registration_number = formData.registration_number || null;
      }
      const res = await staffAPI.onboard(payload);
      const data = res.data || {};
      const loginId = data.login_user_id;
      const tmpPw = data.temporary_password;
      if (loginId && tmpPw) {
        const msg = `${payload.first_name} onboarded successfully!\n\nWelcome credentials (share via Welcome Email):\n• Login ID: ${loginId}\n• Temporary Password: ${tmpPw}\n\nThe employee will be required to set their own password on first login.\nThese credentials will NOT be shown again.`;
        try { await navigator.clipboard.writeText(`Login ID: ${loginId}\nTemporary Password: ${tmpPw}`); } catch {
          // clipboard may be blocked
        }
        alert(msg);
        toast.success('Onboarded · Login ID + temp password copied to clipboard');
        // v38.13 — Surface email send status so the admin knows whether to also
        // share credentials manually (e.g. when Resend is in sandbox mode and
        // can only mail the account holder).
        if (res.data?.welcome_email_sent === true) {
          toast.success('Welcome email sent to ' + payload.email);
        } else if (res.data?.welcome_email_error) {
          toast.warning('Welcome email NOT sent — ' + String(res.data.welcome_email_error).slice(0, 140), { duration: 9000 });
        }
      } else {
        toast.success(`${payload.first_name} onboarded successfully`);
      }
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to onboard staff');
    } finally { setSubmitting(false); }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await staffAPI.seedTestData();
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed data');
    } finally { setSeeding(false); }
  };

  const viewStaff = (s) => { setSelectedStaff(s); setIsViewOpen(true); };

  // Open Edit dialog and prefill from staff record
  const openEdit = (s) => {
    const u = s.user || {};
    setEditForm({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      phone: u.phone || '',
      role_id: u.role_id || '',  // Phase-2 inter-role move support
      department: s.department || '',
      designation: s.designation || '',
      employment_type: s.employment_type || 'full_time',
      blood_group: s.blood_group || '',
      address: s.address || '',
      emergency_contact: s.emergency_contact || '',
      emergency_phone: s.emergency_phone || '',
      pan: s.pan || '',
      aadhaar: s.aadhaar || '',
      status: s.status || 'active',
      assigned_ward_id: s.assigned_ward_id || '',
    });
    setEditStaff(s);
  };

  const setEdit = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));

  const handleSaveEdit = async () => {
    if (!editStaff) return;
    if (!editForm.first_name || !editForm.last_name) { toast.error('Name is required'); return; }
    if (editForm.phone && !/^\d{10}$/.test(editForm.phone)) { toast.error('Phone must be exactly 10 digits'); return; }
    if (editForm.emergency_phone && !/^\d{10}$/.test(editForm.emergency_phone)) { toast.error('Emergency phone must be exactly 10 digits'); return; }
    if (editForm.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(editForm.pan.toUpperCase())) { toast.error('PAN format: ABCDE1234F'); return; }
    if (editForm.aadhaar && !/^\d{12}$/.test(editForm.aadhaar.replace(/\s/g, ''))) { toast.error('Aadhaar must be 12 digits'); return; }
    setSavingEdit(true);
    try {
      const payload = { ...editForm };
      if (payload.pan) payload.pan = payload.pan.toUpperCase();
      if (payload.aadhaar) payload.aadhaar = payload.aadhaar.replace(/\s/g, '');
      // Ward assignment is a separate endpoint — pluck it out and call nurseAPI if changed
      const newWardId = payload.assigned_ward_id;
      delete payload.assigned_ward_id;
      // Drop empty strings so we don't overwrite with blanks accidentally
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      await staffAPI.update(editStaff.id, payload);

      // If the staff is a nurse and ward changed, persist via the nurse-router endpoint
      const isNurse = (editStaff.user?.role_code || '').toLowerCase() === 'nurse';
      const currentWard = editStaff.assigned_ward_id || '';
      if (isNurse && newWardId !== currentWard && editStaff.user?.id) {
        try {
          await nurseAPI.assignWard(editStaff.user.id, newWardId || null);
        } catch (e) {
          // Non-fatal — surface the error but don't roll back staff update
          toast.error(`Profile saved, but ward assignment failed: ${e?.response?.data?.detail || 'unknown'}`);
        }
      }

      toast.success('Staff updated');
      setEditStaff(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update staff');
    } finally { setSavingEdit(false); }
  };

  const handleConfirmDelete = async () => {
    if (!deleteStaff) return;
    setDeleting(true);
    try {
      await staffAPI.delete(deleteStaff.id);
      toast.success(`${deleteStaff.user?.first_name || 'Staff'} deleted permanently`);
      setDeleteStaff(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete staff');
    } finally { setDeleting(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = (list) => {
    setSelectedIds(new Set(list.map(s => s.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkPrint = () => {
    const toPrint = staff.filter(s => selectedIds.has(s.id));
    if (!toPrint.length) { toast.error('Select at least one staff'); return; }
    printBulkIDCards(toPrint, hospital);
    toast.success(`Printing ${toPrint.length} ID card${toPrint.length > 1 ? 's' : ''}`);
  };

  const handleResetAccess = async (s) => {
    if (!s) return;
    const ok = window.confirm(`Reset access for ${s.user?.first_name} ${s.user?.last_name}?\n\nThis will:\n- Invalidate current password\n- Generate a temporary password (shown to you once)\n- Cancel any pending OTPs\n\nThe employee should re-link Google or use the new password.`);
    if (!ok) return;
    try {
      const res = await staffAPI.onboard ? null : null; // placeholder for ts
      const response = await import('../lib/api').then(m => m.authAPI.resetStaffAccess(s.id));
      const data = response.data;
      // Show in a copyable dialog
      const tmp = data.temporary_password;
      const loginId = data.login_user_id || data.email;
      const msg = `Temporary credentials for ${data.email}\n\nLogin ID: ${loginId}\nPassword: ${tmp}\n\nThe employee will be asked to set a new password on first login.\nShare this securely. It will not be shown again.`;
      try { await navigator.clipboard.writeText(`Login ID: ${loginId}\nTemporary Password: ${tmp}`); } catch {
        // clipboard may be blocked
      }
      alert(msg);
      toast.success('Access reset · Temp credentials copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  // Categorize staff by role — DYNAMIC (May 15, 2026): build buckets from the live
  // roles list (which is the same source the Roles & Permissions page reads from).
  // This means: any new role that Super Admin defines auto-shows up as a tab here.
  const codeOf = (s) => ((s.user?.role_code || '').toLowerCase()) ||
    ((s.user?.role_name || '').toLowerCase().replace(/\s+/g, '_'));
  // Skip super_admin in the tabs — hospital staff lists shouldn't display platform-level accounts.
  const tabRoles = (roles || []).filter(r => r.code !== 'super_admin');
  const cat = Object.fromEntries(
    tabRoles.map(r => [r.code, staff.filter(s => codeOf(s) === r.code)])
  );
  // Catch-all: any staff with a role that no longer matches a known role goes into "other"
  const otherStaff = staff.filter(s => {
    const c = codeOf(s);
    return !tabRoles.some(r => r.code === c);
  });

  const getDoctorConfig = (userId) => doctorConfigs.find(d => d.user_id === userId);

  // Role-based gating: Hospital Admin, IT Admin, HR, Super Admin can manage. Delete is stricter.
  const userRole = user?.role_code || user?.role_name?.toLowerCase()?.replace(/\s+/g, '_') || '';
  // Phase-2 permission-first; legacy role list as defence-in-depth.
  const canManageStaff = user?.is_super_admin || hasPermission('staff.manage') ||
    ['hospital_admin', 'hr', 'admin', 'it_admin'].includes(userRole);
  const canChangeRole = user?.is_super_admin || hasPermission('staff.manage') ||
    ['hospital_admin', 'it_admin'].includes(userRole);
  // Only Super Admin, Hospital Admin, IT Admin can DELETE staff (per product owner)
  const canDeleteStaff = user?.is_super_admin || hasPermission('staff.delete') ||
    ['hospital_admin', 'it_admin'].includes(userRole);
  // Item 4/5 — Reset Access + Bulk Reset CSV: IT Admin / Hospital Admin / Super Admin only
  const canResetPasswords = user?.is_super_admin || hasPermission('staff.reset_password') ||
    ['hospital_admin', 'it_admin'].includes(userRole);

  // Item — PCI password policy toggle (per-hospital). IT Admin / Hospital Admin / Super Admin only.
  const [pwPolicyEnforced, setPwPolicyEnforced] = useState(true);
  const [pwPolicyLoaded, setPwPolicyLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const baseURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
        const token = localStorage.getItem('token');
        const hospitalId = localStorage.getItem('selected_hospital_id') || user?.hospital_id;
        if (!hospitalId) return;
        const headers = { 'Authorization': `Bearer ${token}`, 'x-hospital-id': hospitalId };
        const res = await window.fetch(`${baseURL}/api/security/password-policy`, { headers });
        if (res.ok) {
          const d = await res.json();
          setPwPolicyEnforced(d.password_policy_enforced !== false);
          setPwPolicyLoaded(true);
        }
      } catch { /* ignore */ }
    })();
  }, [user]);

  const togglePasswordPolicy = async () => {
    const next = !pwPolicyEnforced;
    const confirmed = window.confirm(
      next
        ? 'Re-enable PCI password policy?\n\nStaff will be required to set passwords with:\n• 12+ characters\n• Uppercase + lowercase + digit + special character\n• No reuse of last 4 passwords'
        : 'Disable PCI password policy?\n\nStaff will be able to set ANY password (minimum 4 characters only).\n\nThis lowers security significantly. Only do this for non-production / low-risk hospitals.'
    );
    if (!confirmed) return;
    try {
      const baseURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      const token = localStorage.getItem('token');
      const hospitalId = localStorage.getItem('selected_hospital_id') || user?.hospital_id;
      const headers = { 'Authorization': `Bearer ${token}`, 'x-hospital-id': hospitalId, 'Content-Type': 'application/json' };
      const res = await window.fetch(`${baseURL}/api/security/password-policy`, { method: 'PUT', headers, body: JSON.stringify({ enforced: next }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Toggle failed' }));
        toast.error(err.detail || 'Toggle failed');
        return;
      }
      setPwPolicyEnforced(next);
      toast.success(next ? 'PCI password policy ENABLED' : 'PCI password policy DISABLED — any password (4+ chars) now accepted');
    } catch (e) {
      toast.error('Toggle failed: ' + (e.message || 'unknown'));
    }
  };

  const handleBulkResetCSV = async () => {
    const confirmed = window.confirm(
      'This will RESET passwords for EVERY staff member in this hospital. They will all be forced to set a new password on next login.\n\nA CSV file with each Employee ID and new temporary password will download — share it securely with each staff member.\n\nContinue?'
    );
    if (!confirmed) return;
    try {
      const baseURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      const token = localStorage.getItem('token');
      const hospitalId = localStorage.getItem('selected_hospital_id') || user?.hospital_id;
      const headers = { 'Authorization': `Bearer ${token}` };
      if (hospitalId) headers['x-hospital-id'] = hospitalId;
      const res = await window.fetch(`${baseURL}/api/staff/bulk-reset-passwords`, { method: 'POST', headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Bulk reset failed' }));
        toast.error(err.detail || 'Bulk reset failed');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `staff_password_reset_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded. Distribute to staff securely.');
    } catch (e) {
      toast.error('Bulk reset failed: ' + (e.message || 'unknown error'));
    }
  };

  if (!canManageStaff) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="staff-no-access">
        <ShieldAlert className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Restricted</h2>
        <p className="text-slate-500 max-w-md">The Staff & HR module is available only to Hospital Admin and HR roles. Please contact your administrator if you need access.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Staff Management</h1>
          <p className="mt-1 text-slate-600 text-sm">
            {staff.length} total{tabRoles.map(r => ` · ${(cat[r.code] || []).length} ${r.name.toLowerCase()}`).join('')}{otherStaff.length > 0 ? ` · ${otherStaff.length} other` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectMode ? (
            <>
              <Button variant="outline" onClick={() => selectAll(staff)} data-testid="select-all-btn">Select All ({staff.length})</Button>
              <Button variant="outline" onClick={clearSelection}>Clear</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleBulkPrint} disabled={!selectedIds.size} data-testid="bulk-print-btn">
                <Printer className="h-4 w-4 mr-2" /> Print {selectedIds.size > 0 ? `(${selectedIds.size})` : 'Selected'}
              </Button>
              <Button variant="outline" onClick={() => { setSelectMode(false); clearSelection(); }} data-testid="exit-select-mode-btn">Cancel</Button>
            </>
          ) : (
            <>
              {staff.length === 0 && (
                <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={handleSeedData} disabled={seeding} data-testid="seed-staff-btn">
                  <Database className="h-4 w-4 mr-2" />
                  {seeding ? 'Seeding...' : 'Add Test Staff'}
                </Button>
              )}
              {staff.length > 0 && (
                <Button variant="outline" onClick={() => setSelectMode(true)} data-testid="bulk-mode-btn">
                  <Printer className="h-4 w-4 mr-2" /> Bulk Print IDs
                </Button>
              )}
              {staff.length > 0 && canResetPasswords && (
                <Button
                  variant="outline"
                  onClick={togglePasswordPolicy}
                  disabled={!pwPolicyLoaded}
                  className={pwPolicyEnforced ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
                  data-testid="toggle-password-policy-btn"
                  title="Toggle PCI password policy for this hospital (IT/Hospital Admin only)"
                >
                  <ShieldCheck className={`h-4 w-4 mr-2 ${pwPolicyEnforced ? '' : 'opacity-40'}`} />
                  PCI Policy: <span className="ml-1 font-semibold">{pwPolicyEnforced ? 'ON' : 'OFF'}</span>
                </Button>
              )}
              {staff.length > 0 && canResetPasswords && (
                <Button
                  variant="outline"
                  onClick={handleBulkResetCSV}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="bulk-reset-csv-btn"
                  title="Reset every staff's password and download a CSV of new credentials"
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Bulk Reset & CSV
                </Button>
              )}
              <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setIsBulkOpen(true)} data-testid="bulk-upload-btn">
                <Upload className="h-4 w-4 mr-2" /> Bulk Upload CSV
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { resetForm(); setIsCreateOpen(true); }} data-testid="create-staff-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Staff
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by name, email, phone, employee code, or department..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10"
              data-testid="staff-search-input" />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-slate-100 flex-wrap h-auto" data-testid="staff-tabs-list">
          <TabsTrigger value="all" data-testid="tab-all-staff"><Users className="h-4 w-4 mr-1.5" /> All ({staff.length})</TabsTrigger>
          {tabRoles.map(r => {
            const Icon = ROLE_TAB_ICON[r.code] || Users;
            return (
              <TabsTrigger key={r.id} value={r.code} data-testid={`tab-${r.code}`}>
                <Icon className="h-4 w-4 mr-1.5" /> {r.name} ({(cat[r.code] || []).length})
              </TabsTrigger>
            );
          })}
          {otherStaff.length > 0 && (
            <TabsTrigger value="__other" data-testid="tab-other"><Users className="h-4 w-4 mr-1.5" /> Other ({otherStaff.length})</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="all"><StaffList staffList={staff} searchQuery={searchQuery} onView={viewStaff} onEdit={openEdit} onDelete={setDeleteStaff} canDelete={canDeleteStaff} emptyMessage="No staff yet. Click 'Add Staff' to onboard your first employee." selectedIds={selectedIds} onToggleSelect={toggleSelect} selectMode={selectMode} /></TabsContent>
        {tabRoles.map(r => (
          <TabsContent key={r.id} value={r.code}>
            <StaffList staffList={cat[r.code] || []} searchQuery={searchQuery} onView={viewStaff} onEdit={openEdit} onDelete={setDeleteStaff} canDelete={canDeleteStaff}
              emptyMessage={`No ${r.name} staff yet.`}
              selectedIds={selectedIds} onToggleSelect={toggleSelect} selectMode={selectMode} />
          </TabsContent>
        ))}
        {otherStaff.length > 0 && (
          <TabsContent value="__other">
            <StaffList staffList={otherStaff} searchQuery={searchQuery} onView={viewStaff} onEdit={openEdit} onDelete={setDeleteStaff} canDelete={canDeleteStaff}
              emptyMessage="No staff with custom roles."
              selectedIds={selectedIds} onToggleSelect={toggleSelect} selectMode={selectMode} />
          </TabsContent>
        )}
      </Tabs>

      {/* Onboarding Dialog (Multi-step) */}
      <Dialog open={isCreateOpen} onOpenChange={(o) => { setIsCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Onboard New Staff</DialogTitle>
            <DialogDescription>Creates user account + staff profile{isDoctorRole ? ' + doctor configuration' : ''} in one step</DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center gap-2 my-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step > i + 1 ? 'bg-emerald-600 text-white' : step === i + 1 ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-100 text-slate-400'
                }`}>{i + 1}</div>
                {i < totalSteps - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > i + 1 ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mb-4 -mt-2">
            Step {step}: {step === 1 ? 'Personal & Account' : step === 2 ? 'Employment' : step === 3 ? 'KYC & Personal' : 'Doctor Configuration'}
          </div>

          <div className="space-y-4">
            {step === 1 && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>First Name *</Label><Input value={formData.first_name} onChange={e => set('first_name', e.target.value)} data-testid="first-name-input" /></div>
                  <div><Label>Last Name *</Label><Input value={formData.last_name} onChange={e => set('last_name', e.target.value)} data-testid="last-name-input" /></div>
                  <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={e => set('email', e.target.value)} placeholder="employee@hospital.com" data-testid="email-input" /></div>
                  <div><Label>Phone <span className="text-[10px] text-slate-400">(10 digits)</span></Label><PhoneInput value={formData.phone} onChange={v => set('phone', v)} required testid="phone-input" /></div>

                  {/* v38.12 — Department moved to Step 1 (drives the Employee ID). */}
                  <div>
                    <Label>Department *</Label>
                    <SearchableSelect
                      value={formData.department}
                      onChange={(v) => {
                        const d = departments.find((x) => x.name === v);
                        set('department', v);
                        set('department_code', (d?.code || '').toUpperCase());
                      }}
                      options={(departments.length > 0
                        ? departments.map(d => ({ value: d.name, label: `${d.name}${d.code ? ` (${d.code})` : ''}` }))
                        : FALLBACK_DEPTS.map(d => ({ value: d, label: d }))
                      )}
                      placeholder="Select department"
                      searchPlaceholder="Search departments…"
                      data-testid="department-select"
                    />
                  </div>
                  <div>
                    <Label>Department Code <span className="text-[10px] text-slate-400">(auto)</span></Label>
                    <Input
                      value={formData.department_code}
                      readOnly
                      disabled
                      className="bg-slate-50 font-mono uppercase cursor-not-allowed"
                      placeholder="Auto-fills from Department"
                      data-testid="department-code-readonly"
                    />
                  </div>

                  {/* v38.12 — Employment Type on Step 1 (drives the Employee ID). */}
                  <div>
                    <Label>Employment Type *</Label>
                    <Select value={formData.staff_category} onValueChange={v => set('staff_category', v)}>
                      <SelectTrigger data-testid="staff-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {STAFF_CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label} ({c.letter})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Designation *</Label>
                    <SearchableSelect
                      value={formData.designation}
                      onChange={(v) => set('designation', v)}
                      options={DESIGNATIONS.map(d => ({ value: d, label: d }))}
                      placeholder="Select designation"
                      searchPlaceholder="Search designations…"
                      data-testid="designation-select"
                    />
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-xs text-teal-800 mt-3">
                  <strong>Login:</strong> A unique <span className="font-semibold">Employee ID</span> in your hospital&apos;s canonical format
                  {' '}<code className="font-mono">&lt;PREFIX&gt;-&lt;DEPT&gt;-&lt;P|C|T&gt;-&lt;SERIAL&gt;</code>
                  {peekId.display_id ? <> (e.g. <code className="font-mono">{peekId.display_id}</code>)</> : <> (e.g. <code className="font-mono">PMSH-DOC-P-1000</code>)</>}
                  {' '}will be auto-generated on the next screen along with a strong temporary password.
                  The employee will be asked to set their own password on first login.
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Employee ID <span className="text-[10px] text-slate-400">(auto-generated · read-only)</span></Label>
                    <Input
                      value={peekId.display_id || ''}
                      readOnly
                      disabled
                      className="bg-emerald-50 border-emerald-200 font-mono text-emerald-800 cursor-not-allowed"
                      placeholder="Pick a Department + Employment Type on Step 1"
                      data-testid="employee-id-readonly"
                    />
                    {peekId.login_id && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        Login form (hyphenless, case-insensitive): <code className="font-mono text-emerald-700">{peekId.login_id}</code>
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Role <span className="text-[10px] text-slate-400">(optional)</span></Label>
                    <Select value={formData.role_id} onValueChange={v => set('role_id', v)}>
                      <SelectTrigger data-testid="role-select"><SelectValue placeholder="Select role (optional)" /></SelectTrigger>
                      <SelectContent>
                        {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Joining Date *</Label><Input type="date" value={formData.joining_date} onChange={e => set('joining_date', e.target.value)} data-testid="joining-date-input" /></div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <div>
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={e => set('address', e.target.value)} placeholder="Full address" data-testid="address-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>PAN Card</Label>
                    <Input value={formData.pan} onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} data-testid="pan-input" />
                  </div>
                  <div>
                    <Label>Aadhaar Number</Label>
                    <Input value={formData.aadhaar} onChange={e => set('aadhaar', e.target.value.replace(/\D/g, ''))} placeholder="12-digit number" maxLength={12} data-testid="aadhaar-input" />
                  </div>
                  <div>
                    <Label>Other ID Type</Label>
                    <Select value={formData.user_id_proof_type} onValueChange={v => set('user_id_proof_type', v)}>
                      <SelectTrigger><SelectValue placeholder="Passport / Driving License..." /></SelectTrigger>
                      <SelectContent>{PROOF_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Other ID Number</Label>
                    <Input value={formData.user_id_proof} onChange={e => set('user_id_proof', e.target.value)} placeholder="ID number" />
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Select value={formData.blood_group} onValueChange={v => set('blood_group', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{BLOOD_GROUPS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Emergency Contact Name</Label><Input value={formData.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} /></div>
                  <div className="md:col-span-2"><Label>Emergency Contact Phone <span className="text-[10px] text-slate-400">(10 digits)</span></Label><PhoneInput value={formData.emergency_phone} onChange={v => set('emergency_phone', v)} testid="emergency-phone-input" /></div>
                </div>
              </div>
            )}

            {step === 4 && isDoctorRole && (
              <div className="space-y-3">
                <p className="text-sm text-emerald-700 bg-emerald-50 rounded p-2">
                  <Stethoscope className="inline h-4 w-4 mr-1" /> Doctor profile will be auto-created. These details power appointment booking & billing.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Specialization</Label><Input value={formData.specialization} onChange={e => set('specialization', e.target.value)} placeholder="e.g., Cardiology" /></div>
                  <div><Label>Qualification</Label><Input value={formData.qualification} onChange={e => set('qualification', e.target.value)} placeholder="MBBS, MD" /></div>
                  <div><Label>Years of Experience</Label><Input type="number" value={formData.experience_years} onChange={e => set('experience_years', e.target.value)} /></div>
                  <div><Label>Registration Number</Label><Input value={formData.registration_number} onChange={e => set('registration_number', e.target.value)} placeholder="Medical council reg no." /></div>
                  <div><Label>Consultation Fee (₹)</Label><Input type="number" value={formData.consultation_fee} onChange={e => set('consultation_fee', e.target.value)} /></div>
                  <div><Label>Follow-up Fee (₹)</Label><Input type="number" value={formData.follow_up_fee} onChange={e => set('follow_up_fee', e.target.value)} /></div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            {step > 1 && <Button type="button" variant="outline" onClick={handleBack}>Back</Button>}
            <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>Cancel</Button>
            {step < totalSteps ? (
              <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleNext} data-testid="onboard-next-btn">Next</Button>
            ) : (
              <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSubmit} disabled={submitting} data-testid="onboard-submit-btn">
                {submitting ? 'Onboarding...' : 'Complete Onboarding'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Staff Profile */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Staff Profile</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <div className="space-y-5 pt-2">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-100 to-lime-100 flex items-center justify-center text-emerald-700 text-xl font-bold border-2 border-emerald-200">
                  {selectedStaff.user?.first_name?.[0]}{selectedStaff.user?.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 truncate">{selectedStaff.user?.first_name} {selectedStaff.user?.last_name}</h3>
                  <p className="text-sm text-slate-500">{selectedStaff.designation}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge className={selectedStaff.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{selectedStaff.status}</Badge>
                    {selectedStaff.user?.role_name && <Badge variant="outline" className="text-xs">{selectedStaff.user.role_name}</Badge>}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => { setIsViewOpen(false); navigate(`/staff-360/${selectedStaff.user?.id}`); }} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="view-staff-360-btn">
                  <Compass className="h-4 w-4 mr-1.5" /> Open Staff 360°
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIdCardStaff(selectedStaff)} data-testid="view-print-id-btn">
                  <Printer className="h-4 w-4 mr-1.5" /> Print ID Card
                </Button>
                <Button size="sm" variant="outline" onClick={() => setWelcomeEmailStaff(selectedStaff)} data-testid="view-welcome-email-btn">
                  <Send className="h-4 w-4 mr-1.5" /> Welcome Email
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleResetAccess(selectedStaff)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="view-reset-access-btn">
                  <ShieldAlert className="h-4 w-4 mr-1.5" /> Reset Access
                </Button>
              </div>

              {/* Login Credentials (May 15, 2026 v31 — P0): IT Admin needs the Employee Login ID
                  visible up-front during troubleshooting, plus a one-click "Generate Temp Password"
                  to skip navigating to the Reset Access button. */}
              {(canResetPasswords || canManageStaff) && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-2" data-testid="login-credentials-block">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Login ID (Employee ID)</p>
                      <p className="font-mono text-lg text-slate-800 truncate" data-testid="view-login-id">{selectedStaff.user?.user_id || '—'}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 shrink-0"
                      onClick={() => navigator.clipboard?.writeText(selectedStaff.user?.user_id || '').then(() => toast.success('Login ID copied'))}
                      data-testid="copy-login-id-btn"
                    >
                      Copy
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleResetAccess(selectedStaff)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="generate-temp-password-btn"
                  >
                    <ShieldAlert className="h-4 w-4 mr-1.5" /> Generate Temp Password (Welcome@123)
                  </Button>
                  <p className="text-[11px] text-emerald-700/80 leading-relaxed">
                    Resets the password to the platform-standard <strong className="font-mono">Welcome@123</strong> and flags the account so the user is forced to change it on next sign-in. Audit-logged.
                  </p>
                </div>
              )}

              {/* Security & Access (May 15, 2026) — visible to admins with staff.manage/reset_password */}
              {(canResetPasswords || canManageStaff) && (
                <SecurityAccessSection staff={selectedStaff} canManage={canResetPasswords || canManageStaff} />
              )}

              {/* Role / Activity History (May 15, 2026 v31) */}
              {(canResetPasswords || canManageStaff) && (
                <RoleHistorySection staff={selectedStaff} />
              )}

              {/* Employment */}
              <Section title="Employment" icon={Briefcase}>
                <Field label="Employee Code" value={selectedStaff.employee_code} mono />
                <Field label="Department" value={selectedStaff.department} />
                <Field label="Joining Date" value={selectedStaff.joining_date} />
                <Field label="Employment Type" value={selectedStaff.employment_type?.replace('_', ' ')} className="capitalize" />
              </Section>

              {/* Contact */}
              <Section title="Contact" icon={Mail}>
                <Field label="Email" value={selectedStaff.user?.email} breakAll />
                <Field label="Phone" value={selectedStaff.user?.phone || '—'} />
                <Field label="Address" value={selectedStaff.address || '—'} fullWidth />
              </Section>

              {/* KYC */}
              {(selectedStaff.pan || selectedStaff.aadhaar || selectedStaff.user_id_proof) && (
                <Section title="KYC / Identification" icon={IdCard}>
                  {selectedStaff.pan && <Field label="PAN" value={selectedStaff.pan} mono />}
                  {selectedStaff.aadhaar && <Field label="Aadhaar" value={`XXXX-XXXX-${selectedStaff.aadhaar.slice(-4)}`} mono />}
                  {selectedStaff.user_id_proof_type && <Field label={`${selectedStaff.user_id_proof_type.replace('_', ' ')}`} value={selectedStaff.user_id_proof || '—'} className="capitalize" />}
                </Section>
              )}

              {/* Personal */}
              {(selectedStaff.blood_group || selectedStaff.emergency_contact) && (
                <Section title="Personal & Emergency" icon={Heart}>
                  {selectedStaff.blood_group && <Field label="Blood Group" value={selectedStaff.blood_group} />}
                  {selectedStaff.emergency_contact && <Field label="Emergency Contact" value={selectedStaff.emergency_contact} />}
                  {selectedStaff.emergency_phone && <Field label="Emergency Phone" value={selectedStaff.emergency_phone} />}
                </Section>
              )}

              {/* Doctor info */}
              {(() => {
                const dc = getDoctorConfig(selectedStaff.user_id);
                if (!dc) return null;
                return (
                  <Section title="Doctor Configuration" icon={Stethoscope}>
                    <Field label="Specialization" value={dc.specialization || '—'} />
                    <Field label="Qualification" value={dc.qualification || '—'} />
                    <Field label="Experience" value={dc.experience_years ? `${dc.experience_years} years` : '—'} />
                    <Field label="Reg. No." value={dc.registration_number || '—'} mono />
                    <Field label="Consultation Fee" value={`₹${dc.consultation_fee || 0}`} />
                    <Field label="Follow-up Fee" value={`₹${dc.follow_up_fee || 0}`} />
                  </Section>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print + Welcome Email Modals */}
      <StaffIDCardModal open={!!idCardStaff} onClose={() => setIdCardStaff(null)} staff={idCardStaff} hospital={hospital} />
      <WelcomeEmailModal open={!!welcomeEmailStaff} onClose={() => setWelcomeEmailStaff(null)} staff={welcomeEmailStaff} hospital={hospital} />

      {/* Bulk Upload Dialog */}
      <Dialog open={isBulkOpen} onOpenChange={(o) => { setIsBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Staff (CSV)</DialogTitle>
            <DialogDescription>
              Required columns: <span className="font-semibold">First Name · Last Name · Email · Phone · Role</span>.
              Employee ID is auto-generated. Default password: <span className="font-mono font-semibold">Welcome@123</span> (forced to change on first login).
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <div className="space-y-4 py-2">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await staffAPI.bulkUploadTemplate();
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url; a.download = 'staff_bulk_upload_template.csv';
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    toast.error('Failed to download template');
                  }
                }}
                data-testid="download-template-btn"
              >
                <FileDown className="h-4 w-4 mr-2" /> Download Sample CSV Template
              </Button>

              <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center">
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                <Label className="block mb-2 text-sm font-medium text-slate-700">Choose your CSV file</Label>
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="max-w-md mx-auto"
                  data-testid="bulk-file-input"
                />
                {bulkFile && <p className="text-xs text-emerald-600 mt-2">Selected: <span className="font-semibold">{bulkFile.name}</span> ({Math.round(bulkFile.size / 1024)} KB)</p>}
              </div>

              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 space-y-1">
                <p className="font-semibold">Accepted role values (case-insensitive code or name):</p>
                <p>doctor · nurse · reception (or "Front Desk") · billing_staff · accountant · hospital_admin · hr · it_admin</p>
                <p className="mt-2 font-semibold">Optional columns:</p>
                <p>department, designation, employment_type, blood_group, address, emergency_contact, emergency_phone, pan, aadhaar, gender, date_of_birth</p>
              </div>
            </div>
          )}

          {bulkResult && (
            <div className="space-y-3 py-2" data-testid="bulk-upload-result">
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">{bulkResult.summary?.total_rows}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Rows</p>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{bulkResult.summary?.created}</p>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-600">Created</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{bulkResult.summary?.duplicate_emails}</p>
                  <p className="text-[10px] uppercase tracking-wider text-amber-600">Dup Emails</p>
                </div>
                <div className="rounded-lg bg-red-50 p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{bulkResult.summary?.errors}</p>
                  <p className="text-[10px] uppercase tracking-wider text-red-600">Errors</p>
                </div>
              </div>

              {bulkResult.created?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1"><CheckCircle2 size={14} className="text-emerald-600" /> Created</p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr><th className="px-2 py-1.5 text-left">Row</th><th className="px-2 py-1.5 text-left">Employee ID</th><th className="px-2 py-1.5 text-left">Name</th><th className="px-2 py-1.5 text-left">Email</th><th className="px-2 py-1.5 text-left">Role</th></tr>
                      </thead>
                      <tbody>
                        {bulkResult.created.map((c, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1 text-slate-500">{c.row}</td>
                            <td className="px-2 py-1 font-mono font-semibold">{c.employee_id}</td>
                            <td className="px-2 py-1">{c.name}</td>
                            <td className="px-2 py-1 text-slate-600">{c.email}</td>
                            <td className="px-2 py-1"><span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px]">{c.role}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">All accounts use password <span className="font-mono font-semibold">{bulkResult.default_password}</span>. Share it with each user — they will be forced to change on first login.</p>
                </div>
              )}

              {bulkResult.duplicates?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1"><AlertTriangle size={14} className="text-amber-600" /> Duplicate emails (still imported)</p>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/50 p-2 space-y-1">
                    {bulkResult.duplicates.map((d, i) => (
                      <p key={i} className="text-xs">Row {d.row} · <span className="font-mono">{d.email}</span> — {d.reason}</p>
                    ))}
                  </div>
                </div>
              )}

              {bulkResult.errors?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1"><XCircle size={14} className="text-red-600" /> Errors (skipped)</p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50/50 p-2 space-y-1">
                    {bulkResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-800">Row {e.row}: {e.reason}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {bulkResult ? (
              <>
                <Button variant="outline" onClick={() => { setBulkResult(null); setBulkFile(null); }} data-testid="upload-another">Upload Another</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setIsBulkOpen(false); setBulkResult(null); setBulkFile(null); }} data-testid="close-bulk-result">Done</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsBulkOpen(false)}>Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!bulkFile || bulkUploading}
                  onClick={async () => {
                    setBulkUploading(true);
                    try {
                      const res = await staffAPI.bulkUpload(bulkFile);
                      setBulkResult(res.data);
                      toast.success(res.data?.message || 'Upload complete');
                      fetchData(); // refresh staff list
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Bulk upload failed');
                    } finally {
                      setBulkUploading(false);
                    }
                  }}
                  data-testid="bulk-upload-submit"
                >
                  {bulkUploading ? 'Uploading…' : <><Upload className="h-4 w-4 mr-2" /> Upload & Create</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={!!editStaff} onOpenChange={(o) => !o && setEditStaff(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-staff-dialog">
          <DialogHeader>
            <DialogTitle>Edit Staff Details</DialogTitle>
            <DialogDescription>
              {canChangeRole
                ? "Update profile fields. You can also flip this person's role (e.g., Nurse → Front Desk) — permissions update immediately on their next request."
                : "Update profile fields. Email and Role are not editable here — to change those, deactivate this record and onboard a fresh account."}
            </DialogDescription>
          </DialogHeader>
          {editStaff && (
            <div className="space-y-4 mt-2">
              {/* Role change (admin-only) — placed first so the most-impactful field is obvious */}
              {canChangeRole && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="edit-role-block">
                  <Label className="text-xs uppercase tracking-wider text-emerald-700">Role · inter-role move</Label>
                  <p className="text-[11px] text-emerald-700/80 mb-2">
                    Flip this staff member to another role within <strong>{hospital?.name || 'this hospital'}</strong>. Their permission set updates instantly on next request.
                  </p>
                  <Select value={editForm.role_id || ''} onValueChange={v => setEdit('role_id', v)}>
                    <SelectTrigger data-testid="edit-role-select"><SelectValue placeholder="Pick a role…" /></SelectTrigger>
                    <SelectContent>
                      {(roles || []).filter(r => r.code !== 'super_admin').map(r =>
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {editStaff.user?.role_name && editForm.role_id && editForm.role_id !== editStaff.user.role_id && (
                    <p className="text-[11px] text-amber-700 mt-2">
                      ⚠️ Role will change from <strong>{editStaff.user.role_name}</strong> to the selected role on Save. Audit-logged.
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>First Name *</Label><Input value={editForm.first_name || ''} onChange={e => setEdit('first_name', e.target.value)} data-testid="edit-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={editForm.last_name || ''} onChange={e => setEdit('last_name', e.target.value)} data-testid="edit-last-name" /></div>
                <div><Label>Email <span className="text-[10px] text-slate-400">(read-only)</span></Label><Input value={editStaff.user?.email || ''} disabled className="bg-slate-50" /></div>
                <div><Label>Phone <span className="text-[10px] text-slate-400">(10 digits)</span></Label><PhoneInput value={editForm.phone || ''} onChange={v => setEdit('phone', v)} testid="edit-phone" /></div>
                <div><Label>Department</Label>
                  <SearchableSelect
                    value={editForm.department || ''}
                    onChange={v => setEdit('department', v)}
                    options={(departments.length ? departments : FALLBACK_DEPTS.map(d => ({ id: d, name: d }))).map(d => ({ value: d.name, label: d.name }))}
                    placeholder="Select department"
                    searchPlaceholder="Search departments…"
                    data-testid="edit-department"
                  />
                </div>
                <div><Label>Designation</Label><Input value={editForm.designation || ''} onChange={e => setEdit('designation', e.target.value)} data-testid="edit-designation" /></div>
                <div><Label>Employment Type</Label>
                  <Select value={editForm.employment_type || 'full_time'} onValueChange={v => setEdit('employment_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={editForm.status || 'active'} onValueChange={v => setEdit('status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on_leave">On Leave</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label>Address</Label><Input value={editForm.address || ''} onChange={e => setEdit('address', e.target.value)} /></div>
                <div><Label>PAN</Label><Input value={editForm.pan || ''} onChange={e => setEdit('pan', e.target.value)} placeholder="ABCDE1234F" className="font-mono uppercase" /></div>
                <div><Label>Aadhaar</Label><Input value={editForm.aadhaar || ''} onChange={e => setEdit('aadhaar', e.target.value)} placeholder="12 digits" className="font-mono" /></div>
                <div><Label>Blood Group</Label>
                  <Select value={editForm.blood_group || ''} onValueChange={v => setEdit('blood_group', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{BLOOD_GROUPS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Emergency Contact</Label><Input value={editForm.emergency_contact || ''} onChange={e => setEdit('emergency_contact', e.target.value)} /></div>
                <div><Label>Emergency Phone <span className="text-[10px] text-slate-400">(10 digits)</span></Label><PhoneInput value={editForm.emergency_phone || ''} onChange={v => setEdit('emergency_phone', v)} testid="edit-emergency-phone" /></div>
              </div>
              {/* Nurse-only: ward assignment (Phase D — surfaces /api/nurse/{id}/assign-ward) */}
              {(editStaff.user?.role_code || '').toLowerCase() === 'nurse' && (
                <div className="md:col-span-2 rounded-lg border border-blue-200 bg-blue-50/40 p-3" data-testid="nurse-ward-assignment">
                  <Label className="text-xs uppercase tracking-wider text-blue-700">Assigned Ward (Nurse Workstation scope)</Label>
                  <p className="text-[11px] text-blue-600 mb-2">When set, this nurse's dashboard will scope to admitted patients in this ward by default.</p>
                  <Select value={editForm.assigned_ward_id || '__none__'} onValueChange={v => setEdit('assigned_ward_id', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="edit-ward"><SelectValue placeholder="Select a ward (or none)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No ward (sees all wards) —</SelectItem>
                      {wards.map(w => <SelectItem key={w.id} value={w.id}>{w.name}{w.ward_type ? ` · ${w.ward_type}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditStaff(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-edit-btn">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteStaff} onOpenChange={(o) => !o && setDeleteStaff(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-staff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-5 w-5" /> Delete Staff Member?</DialogTitle>
            <DialogDescription>
              This <strong>permanently removes</strong> the user account, staff profile, and any role-specific config (doctor schedule, attendance, leaves). The email and employee code become available for re-use. This cannot be undone — the action is captured in the audit log for compliance.
            </DialogDescription>
          </DialogHeader>
          {deleteStaff && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
              <p className="text-sm font-semibold text-slate-900">{deleteStaff.user?.first_name} {deleteStaff.user?.last_name}</p>
              <p className="text-xs text-slate-500">{deleteStaff.designation} · {deleteStaff.department}</p>
              <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{deleteStaff.employee_code}</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteStaff(null)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-slate-50/50 rounded-lg p-4">
    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {title}
    </h4>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, mono, fullWidth, breakAll, className }) => (
  <div className={`min-w-0 ${fullWidth ? 'col-span-2' : ''}`}>
    <p className="text-xs text-slate-500 mb-0.5">{label}</p>
    <p className={`text-sm font-medium text-slate-800 ${mono ? 'font-mono' : ''} ${breakAll ? 'break-all' : 'break-words'} ${className || ''}`}>{value}</p>
  </div>
);

/**
 * SecurityAccessSection — admin-only panel inside a staff profile.
 * Shows live security state (last login, last password reset, lock status,
 * failed login attempts, must-change flag) and provides an "Unlock now" CTA
 * to clear a 30-min auto-lock without waiting it out.
 */
const SecurityAccessSection = ({ staff, canManage }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);

  const refresh = useCallback(async () => {
    if (!staff?.id) return;
    setLoading(true);
    try {
      const { data } = await staffAPI.accountStatus(staff.id);
      setStatus(data);
    } catch (err) {
      // Silent fail — show "—" placeholders. Don't toast on every profile open.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [staff?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      await staffAPI.unlockAccount(staff.id);
      toast.success('Account unlocked. The user can sign in immediately.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not unlock account');
    } finally {
      setUnlocking(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return format(new Date(iso), 'MMM d, yyyy · HH:mm'); }
    catch { return iso; }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="security-access-section">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5" /> Security &amp; Access
        </h4>
        <Button size="sm" variant="ghost" onClick={refresh} disabled={loading} data-testid="security-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {loading && !status ? (
        <div className="text-sm text-slate-400 italic py-2">Loading account status…</div>
      ) : !status ? (
        <div className="text-sm text-slate-400 italic py-2">Status unavailable (insufficient permission, or fetch failed).</div>
      ) : (
        <>
          {/* Lock status row — most important */}
          <div className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border ${status.is_locked ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="security-lock-row">
            <div className="flex items-center gap-2 min-w-0">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-rose-500"
                  checked={status.is_locked}
                  onChange={() => status.is_locked && canManage && unlock()}
                  disabled={!status.is_locked || !canManage || unlocking}
                  data-testid="security-lock-checkbox"
                />
                <span className="text-sm font-medium text-slate-700">
                  {status.is_locked ? 'Account is LOCKED' : 'Account is unlocked'}
                </span>
              </label>
              {status.locked_until && status.is_locked && (
                <span className="text-xs text-rose-600 truncate">· lock lifts at {fmt(status.locked_until)}</span>
              )}
            </div>
            {status.is_locked && canManage && (
              <Button size="sm" onClick={unlock} disabled={unlocking}
                className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-3 text-xs"
                data-testid="security-unlock-btn">
                {unlocking ? 'Unlocking…' : 'Unlock now'}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-1">
            <Field label="Last Login" value={fmt(status.last_login)} />
            <Field label="Last Password Reset" value={fmt(status.last_password_reset_at)} />
            <Field
              label="Failed Login Attempts"
              value={
                status.failed_login_attempts > 0
                  ? `${status.failed_login_attempts} (auto-locks at 5)`
                  : '0'
              }
            />
            <Field
              label="Must Change Password"
              value={status.must_change_password ? 'Yes — on next login' : 'No'}
            />
            {status.last_password_reset_by && (
              <Field label="Last Reset By" value={status.last_password_reset_by} fullWidth breakAll />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StaffPage;

/**
 * RoleHistorySection — admin-only timeline of role changes, unlocks, resets, etc.
 * Pulls from /api/staff/{id}/history (audit logs filtered to security-relevant actions).
 */
const RoleHistorySection = ({ staff }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!staff?.id || !show) return;
    let mounted = true;
    setLoading(true);
    staffAPI.history(staff.id)
      .then(({ data }) => { if (mounted) setItems(data || []); })
      .catch(() => { if (mounted) setItems([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [staff?.id, show]);

  const labelFor = {
    ROLE_CHANGE: { label: 'Role Change', color: 'bg-indigo-100 text-indigo-700' },
    UNLOCK_ACCOUNT: { label: 'Account Unlocked', color: 'bg-rose-100 text-rose-700' },
    RESET_ACCESS: { label: 'Access Reset', color: 'bg-amber-100 text-amber-700' },
    BULK_RESET: { label: 'Bulk Reset', color: 'bg-amber-100 text-amber-700' },
    PASSWORD_CHANGE: { label: 'Password Changed', color: 'bg-emerald-100 text-emerald-700' },
    UPDATE: { label: 'Profile Updated', color: 'bg-slate-100 text-slate-700' },
    LOGIN: { label: 'Logged In', color: 'bg-blue-50 text-blue-700' },
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="role-history-section">
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="w-full text-left flex items-center justify-between"
        data-testid="role-history-toggle"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Role &amp; Activity History
        </h4>
        <span className="text-xs text-emerald-600">{show ? 'Hide' : 'Show'}</span>
      </button>
      {show && (
        <>
          {loading ? (
            <div className="text-sm text-slate-400 italic py-2">Loading history…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-2">No recorded activity for this staff member yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it, i) => {
                const meta = labelFor[it.action] || { label: it.action, color: 'bg-slate-100 text-slate-700' };
                let when = '—';
                try { when = format(new Date(it.created_at), 'MMM d, yyyy · HH:mm'); } catch {}
                const detail = it.details || {};
                let extra = '';
                if (it.action === 'ROLE_CHANGE') extra = `→ ${detail.new_role || '?'}`;
                else if (it.action === 'UPDATE' && Object.keys(detail).length) extra = `(${Object.keys(detail).slice(0, 3).join(', ')})`;
                return (
                  <li key={i} className="py-2 flex items-start gap-2 text-sm" data-testid={`role-history-row-${i}`}>
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-700">
                        {extra && <span className="text-slate-500">{extra}</span>}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {when}{it.user_email ? ` · by ${it.user_email}` : ''}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
